import { RJSFSchema, UiSchema } from '@rjsf/utils';

import {
  ActionGroup,
  SubAction,
} from 'dashboard/components/WorkflowControlPanel/StepConfigAction/selectActionUtils';
import {
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
  WorkflowObjectKey,
  WorkflowOperatorKey,
} from 'dashboard/models/workflow';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import {
  FieldSchemaArray,
  FieldSchemaBoolean,
  FieldSchemaChoices,
  FieldSchemaDate,
  FieldSchemaFile,
  FieldSchemaHash,
  FieldSchemaInteger,
  FieldSchemaJson,
  FieldSchemaMultiChoices,
  FieldSchemaNumber,
  FieldSchemaString,
  FieldSchemaType,
  JsonSchemaCustomTypes,
} from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';

export type WorkflowCriteriaVariableSchemas = Partial<
  Record<WorkflowObjectKey, WorkflowObjectSchemas>
>;

export interface WorkflowObjectSchemas {
  common: Partial<WorkflowFieldSchemas>;
  'with-template': Record<string, Partial<WorkflowFieldSchemas>>;
  associations: { cardinality: 'one' | 'many'; key: string; type: WorkflowFieldObjectKey }[];
}

export interface WorkflowFieldSchemas {
  [WorkflowFieldKey.AbuseScore]: FieldSchemaInteger;
  [WorkflowFieldKey.AccountStatus]: FieldSchemaChoices;
  [WorkflowFieldKey.AddressCategoryRiskScoreLevel]: FieldSchemaInteger;
  [WorkflowFieldKey.AddressRiskScore]: FieldSchemaString;
  [WorkflowFieldKey.AdverseMediaTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.Age]: FieldSchemaInteger;
  [WorkflowFieldKey.AnyPriorInquiryStatus]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.BehaviorThreatLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.BrowserCount]: FieldSchemaInteger;
  [WorkflowFieldKey.BrowserFingerprints]: FieldSchemaArray;
  [WorkflowFieldKey.BrowserTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.CaptureMethods]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.CarrierRouteType]: FieldSchemaChoices;
  [WorkflowFieldKey.CaseResolution]: FieldSchemaChoices;
  [WorkflowFieldKey.CaseState]: FieldSchemaChoices;
  [WorkflowFieldKey.ClientMobileSdkVersion]: FieldSchemaString;
  [WorkflowFieldKey.ClientOsName]: FieldSchemaString;
  [WorkflowFieldKey.CommercialMailReceivingAgency]: FieldSchemaBoolean;
  [WorkflowFieldKey.Country]: FieldSchemaChoices;
  [WorkflowFieldKey.CountryComparison]: FieldSchemaBoolean;
  [WorkflowFieldKey.CountryFromSession]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.AddressCityMatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.AddressPostalCodeMatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.AddressStreet1MatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.AddressSubdivisionMatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.BusinessNameMatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.RegistrationNumberMatchLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.Data]: FieldSchemaFile;
  [WorkflowFieldKey.DatabaseTinVerificationTinType]: FieldSchemaChoices;
  [WorkflowFieldKey.DatabaseVerificationAddressMatch]: FieldSchemaChoices;
  [WorkflowFieldKey.DatabaseVerificationIdentityMatchFailed]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.DatabaseVerificationSsnEditDistance]: FieldSchemaInteger;
  [WorkflowFieldKey.DeviceCount]: FieldSchemaInteger;
  [WorkflowFieldKey.DeviceFingerprints]: FieldSchemaArray;
  [WorkflowFieldKey.DeviceNames]: FieldSchemaArray;
  [WorkflowFieldKey.DistractionEvents]: FieldSchemaInteger;
  [WorkflowFieldKey.DocumentCheckScore]: FieldSchemaNumber;
  [WorkflowFieldKey.DocumentExtractions]: FieldSchemaHash;
  [WorkflowFieldKey.DocumentSimilarityScore]: FieldSchemaNumber;
  [WorkflowFieldKey.DocumentTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.EmailAddress]: FieldSchemaString;
  [WorkflowFieldKey.EmailAddressDomain]: FieldSchemaString;
  [WorkflowFieldKey.EmailAddressDomainCreatedDays]: FieldSchemaInteger;
  [WorkflowFieldKey.EmailAddressDomainExists]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressDomainReputations]: FieldSchemaChoices;
  [WorkflowFieldKey.EmailAddressFirstSeenDays]: FieldSchemaInteger;
  [WorkflowFieldKey.EmailAddressEstimatedAgeDays]: FieldSchemaInteger;
  [WorkflowFieldKey.EmailAddressIsBlacklisted]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsDeliverable]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsDisposable]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsFreeProvider]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsSpam]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsSuspicious]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressIsValidMx]: FieldSchemaBoolean;
  [WorkflowFieldKey.EmailAddressLastSeenDays]: FieldSchemaInteger;
  [WorkflowFieldKey.EmailAddressReferenceCount]: FieldSchemaInteger;
  [WorkflowFieldKey.EmailAddressReputations]: FieldSchemaChoices;
  [WorkflowFieldKey.FacebookUrl]: FieldSchemaString;
  [WorkflowFieldKey.FailedAamvaVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedChecks]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.FailedChecksFromLatestVerifications]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.FailedDatabaseVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedDatabaseBusinessVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedDatabaseEcbsvVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedDocumentVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedElectronicReplicaReasons]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.FailedGovernmentIdVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedMatchGroup]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.FailedSelfieVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.FailedVerificationAttempts]: FieldSchemaInteger;
  [WorkflowFieldKey.Fields]: FieldSchemaHash;
  [WorkflowFieldKey.FicoScore8EquifaxDataNonFacta]: FieldSchemaInteger;
  [WorkflowFieldKey.FicoScore9EquifaxDataNonFacta]: FieldSchemaInteger;
  [WorkflowFieldKey.FilledProperties]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.FlowState]: FieldSchemaJson;
  [WorkflowFieldKey.FirstIntegrationType]: FieldSchemaChoices;
  [WorkflowFieldKey.FirstPartySyntheticScore]: FieldSchemaInteger;
  [WorkflowFieldKey.Geolocations]: FieldSchemaArray;
  [WorkflowFieldKey.GeolocationToResidencyDelta]: FieldSchemaNumber;
  [WorkflowFieldKey.GovernmentIdDesignations]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GovernmentIdExpirationDate]: FieldSchemaDate;
  [WorkflowFieldKey.GovernmentIdExpirationDelta]: FieldSchemaInteger;
  [WorkflowFieldKey.GovernmentIdExpired]: FieldSchemaBoolean;
  [WorkflowFieldKey.GovernmentIdInquiryComparisonMatchFailed]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GovernmentIdTags]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GovernmentIdVisaStatus]: FieldSchemaString;
  [WorkflowFieldKey.GraphQueryAccountStatuses]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GraphQueryAccountTags]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GraphQueryAccountTokens]: FieldSchemaArray;
  [WorkflowFieldKey.GraphQueryInquiryStatuses]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.GraphQueryNumberOfAccountNodes]: FieldSchemaInteger;
  [WorkflowFieldKey.HasDocument]: FieldSchemaBoolean;
  [WorkflowFieldKey.HasGovernmentIdExtraction]: FieldSchemaBoolean;
  [WorkflowFieldKey.HasSelfie]: FieldSchemaBoolean;
  [WorkflowFieldKey.HasStarted]: FieldSchemaBoolean;
  [WorkflowFieldKey.HasUnknownId]: FieldSchemaBoolean;
  [WorkflowFieldKey.HighestSimilarityLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.IncognitoBrowsingDetected]: FieldSchemaBoolean;
  [WorkflowFieldKey.RiskLabel]: FieldSchemaChoices;
  [WorkflowFieldKey.Id]: FieldSchemaString;
  [WorkflowFieldKey.IdTheftScore]: FieldSchemaInteger;
  [WorkflowFieldKey.IdClasses]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.IpAddresses]: FieldSchemaArray;
  [WorkflowFieldKey.IpCount]: FieldSchemaInteger;
  [WorkflowFieldKey.IpIspList]: FieldSchemaArray;
  [WorkflowFieldKey.IpServiceType]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.IsContinuousMatch]: FieldSchemaBoolean;
  [WorkflowFieldKey.LastTwoVerificationsGeolocationDelta]: FieldSchemaNumber;
  [WorkflowFieldKey.LatestSessionDevice]: FieldSchemaChoices;
  [WorkflowFieldKey.LinkedinId]: FieldSchemaString;
  [WorkflowFieldKey.LinkedinUrl]: FieldSchemaString;
  [WorkflowFieldKey.LinkUrl]: FieldSchemaString;
  [WorkflowFieldKey.ListMatches]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.LivenessCheckScore]: FieldSchemaNumber;
  [WorkflowFieldKey.MatchLevels]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.MatchStatusAddress]: FieldSchemaChoices;
  [WorkflowFieldKey.MatchStatusAssociatedPerson]: FieldSchemaChoices;
  [WorkflowFieldKey.MatchStatusBusinessName]: FieldSchemaChoices;
  [WorkflowFieldKey.MatchStatusEin]: FieldSchemaChoices;
  [WorkflowFieldKey.MatchStatusStrength]: FieldSchemaChoices;
  [WorkflowFieldKey.MatchFound]: FieldSchemaBoolean;
  [WorkflowFieldKey.MatchTags]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.MatchTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.Name]: FieldSchemaChoices;
  [WorkflowFieldKey.NameFirst]: FieldSchemaString;
  [WorkflowFieldKey.NameLast]: FieldSchemaString;
  [WorkflowFieldKey.PassedDatabaseVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.PassedDatabaseEcbsvVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.PassedGovernmentIdVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.PassedSelfieVerificationCount]: FieldSchemaInteger;
  [WorkflowFieldKey.PhoneCarrier]: FieldSchemaString;
  [WorkflowFieldKey.PhoneCountryCode]: FieldSchemaChoices;
  [WorkflowFieldKey.PhoneNumber]: FieldSchemaString;
  [WorkflowFieldKey.PhoneRiskRecommendations]: FieldSchemaChoices;
  [WorkflowFieldKey.PhoneType]: FieldSchemaChoices;
  [WorkflowFieldKey.PoBox]: FieldSchemaBoolean;
  [WorkflowFieldKey.PoliticallyExposedPersonClassTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.ProxyDetected]: FieldSchemaBoolean;
  [WorkflowFieldKey.QueryAddressTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.QueryAddressVacancyStatus]: FieldSchemaChoices;
  [WorkflowFieldKey.Reasons]: FieldSchemaArray;
  [WorkflowFieldKey.RegistrationStatus]: FieldSchemaChoices;
  [WorkflowFieldKey.ReferenceId]: FieldSchemaString;
  [WorkflowFieldKey.RelevanceTypes]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.ReportCountries]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.ResidentialDeliveryIndicator]: FieldSchemaChoices;
  [WorkflowFieldKey.RiskLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.RiskScore]: FieldSchemaInteger;
  [WorkflowFieldKey.SelectedCountry]: FieldSchemaChoices;
  [WorkflowFieldKey.SelfieLivenessRiskLevel]: FieldSchemaChoices;
  [WorkflowFieldKey.SelfiePhoto]: FieldSchemaFile;
  [WorkflowFieldKey.Selfies]: FieldSchemaJson;
  [WorkflowFieldKey.SessionCount]: FieldSchemaInteger;
  [WorkflowFieldKey.SessionsGeolocationDelta]: FieldSchemaNumber;
  [WorkflowFieldKey.SimilarAccountsBySelfie]: FieldSchemaNumber;
  [WorkflowFieldKey.SsnDigitCount]: FieldSchemaInteger;
  [WorkflowFieldKey.Status]: FieldSchemaChoices;
  [WorkflowFieldKey.SyntheticScore]: FieldSchemaInteger;
  [WorkflowFieldKey.Tags]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.TinType]: FieldSchemaChoices;
  [WorkflowFieldKey.ThirdPartySyntheticScore]: FieldSchemaInteger;
  [WorkflowFieldKey.ThreatLevels]: FieldSchemaMultiChoices;
  [WorkflowFieldKey.Token]: FieldSchemaChoices;
  [WorkflowFieldKey.TorDetected]: FieldSchemaBoolean;
  [WorkflowFieldKey.TrustedDevices]: FieldSchemaArray;
  [WorkflowFieldKey.TwitterUrl]: FieldSchemaString;
  [WorkflowFieldKey.UserRiskScore]: FieldSchemaString;
  [WorkflowFieldKey.UsState]: FieldSchemaChoices;
  [WorkflowFieldKey.VantageScore4]: FieldSchemaInteger;
  [WorkflowFieldKey.ZipType]: FieldSchemaChoices;
}

export interface WorkflowRedactableFieldConfigField {
  path: string;
  label: string;
  'template-name': string;
  'template-id': string;
}

export interface WorkflowRedactableFieldConfig {
  token: string;
  fields: WorkflowRedactableFieldConfigField[];
}

export interface WorkflowFilterFieldConfigs {
  [ApiModel.Account]: WorkflowFilterFieldConfig[];
  [ApiModel.Case]: WorkflowFilterFieldConfig[];
  [ApiModel.Inquiry]: WorkflowFilterFieldConfig[];
  [ApiModel.Report]: WorkflowFilterFieldConfig[];
  [ApiModel.SAR]: WorkflowFilterFieldConfig[];
  [ApiModel.Transaction]: WorkflowFilterFieldConfig[];
}

export interface WorkflowFilterFieldConfig {
  name: string;
  config: {
    type: string;
    operators: { label: string; value: string }[];
    units: { label: string; value: string }[];
    options: { label: string; value: string }[];
  };
}
export type HandlebarsFieldType =
  | JsonSchemaTypes.Boolean
  | JsonSchemaTypes.Integer
  | JsonSchemaTypes.Number
  | JsonSchemaTypes.String
  | JsonSchemaCustomTypes.Date
  | JsonSchemaCustomTypes.Enum;

export type AllowedObjectType =
  | WorkflowFieldObjectKey.Account
  | WorkflowFieldObjectKey.Case
  | WorkflowFieldObjectKey.GraphQuery
  | WorkflowFieldObjectKey.Inquiry
  | WorkflowFieldObjectKey.ReportAddressLookup
  | WorkflowFieldObjectKey.ReportAdverseMedia
  | WorkflowFieldObjectKey.ReportBusinessAdverseMedia
  | WorkflowFieldObjectKey.ReportBusinessAssociatedPersons
  | WorkflowFieldObjectKey.ReportBusinessIndustryClassification
  | WorkflowFieldObjectKey.ReportBusinessLiensLookup
  | WorkflowFieldObjectKey.ReportBusinessLookup
  | WorkflowFieldObjectKey.ReportBusinessRegistrationsLookup
  | WorkflowFieldObjectKey.ReportBusinessSocialMedia
  | WorkflowFieldObjectKey.ReportBusinessWatchlist
  | WorkflowFieldObjectKey.ReportBusinessWebsite
  | WorkflowFieldObjectKey.ReportChainalysisAddressScreening
  | WorkflowFieldObjectKey.ReportClearbitBusinessLookup
  | WorkflowFieldObjectKey.ReportCryptoAddressWatchlist
  | WorkflowFieldObjectKey.ReportCustomList
  | WorkflowFieldObjectKey.ReportEmailAddress
  | WorkflowFieldObjectKey.ReportEquifaxOneview
  | WorkflowFieldObjectKey.ReportKyckrBusinessLookup
  | WorkflowFieldObjectKey.ReportMxAccount
  | WorkflowFieldObjectKey.ReportPhoneNumber
  | WorkflowFieldObjectKey.ReportPoliticallyExposedPerson
  | WorkflowFieldObjectKey.ReportProfile
  | WorkflowFieldObjectKey.ReportProfileNonAuthoritative
  | WorkflowFieldObjectKey.ReportScreening
  | WorkflowFieldObjectKey.ReportSentilinkApplicationRisk
  | WorkflowFieldObjectKey.ReportSentilinkScores
  | WorkflowFieldObjectKey.ReportSocialMedia
  | WorkflowFieldObjectKey.ReportSynthetic
  | WorkflowFieldObjectKey.ReportTrmWalletScreening
  | WorkflowFieldObjectKey.ReportWatchlist
  | WorkflowFieldObjectKey.SAR
  | WorkflowFieldObjectKey.Transaction
  | WorkflowFieldObjectKey.VerificationDatabase
  | WorkflowFieldObjectKey.VerificationDatabaseBusiness
  | WorkflowFieldObjectKey.VerificationDatabaseStandard
  | WorkflowFieldObjectKey.VerificationDatabaseTin
  | WorkflowFieldObjectKey.VerificationDatabaseVatNumber
  | WorkflowFieldObjectKey.VerificationDocument
  | WorkflowFieldObjectKey.VerificationGovernmentId;

export type TemplateTypeWithFieldSchemas = 'account-types' | 'case-templates' | 'transaction-types';
export type TemplateTypeWithSubtypes = 'report-templates' | 'lists';
export type AllowedActionMetaType =
  | TemplateTypeWithFieldSchemas
  | TemplateTypeWithSubtypes
  | 'account-statuses'
  | 'case-queues'
  | 'case-states'
  | 'transaction-statuses'
  | 'users';

export enum GenericWorkflowActionEnumType {
  Object = 'object',
  Meta = 'meta',
}

export enum UiSchemaWidgetTypes {
  Select = 'select',
}

export enum UiSchemaFieldTypes {
  Handlebars = 'handlebars',
  FieldsMapping = 'fieldsMapping',
  Checkbox = 'checkbox',
  NotSupported = 'notSupported',
}

export interface GenericWorkflowAction {
  title: string;
  key: string;
  category: ActionGroup;
  subCategory?: SubAction;
  enabled: boolean;
  request: {
    schema: RJSFSchema;
    uiSchema: UiSchema;
  };
}

export type WorkflowOperators = Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;

export interface WorkflowSchema {
  'criteria-variable-schemas': WorkflowCriteriaVariableSchemas;
  operators: WorkflowOperators;
  'filter-field-configs': WorkflowFilterFieldConfigs;
  'generic-workflow-actions': GenericWorkflowAction[];
  primitives: ObjectSchemaMap;
}
